import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { TotalReachLogo } from "./Logo";
import { OrgSwitcher } from "./OrgSwitcher";
import { AiDisclaimerGate } from "./AiDisclaimer";
import { AskTotalReachWidget } from "./AskTotalReachWidget";
import { usePlan } from "./UpgradeGate";
import { UpgradeNudge } from "./UpgradeNudge";
import { AnnualUpgradeToast } from "./AnnualUpgradeNudge";
import { api, auth } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { toast } from "sonner";
import {
  LayoutDashboard, Radio as WarRoomIcon, Megaphone, Tv, Monitor,
  ListChecks, Layers, Vote, Users2, Database, MapPinned, Compass,
  HandCoins, LayoutTemplate, Wand2, ScrollText, LineChart, Activity,
  ShieldCheck, FileCheck2, Landmark, Wallet, Users, Settings, LogOut,
  Search, ChevronDown, Sparkles, Radio, Building2, Send, HeartHandshake,
  Receipt, FileText, Globe, Clapperboard, HelpCircle, Gauge, KeyRound, Loader2, X, UserRound,
  Mail, Mailbox, ArrowUpRight, Route, BrainCircuit, CalendarDays, QrCode, TrendingUp, Briefcase, Palette, MessageSquare, Trophy, Star, Plus, BookOpen, Menu as MenuIcon, Rocket, Gift
} from "lucide-react";
import { FeatureToggleModal } from "./FeatureToggleModal";
import { SimpleNavList, SimpleBottomBar } from "./SimpleNav";
import { GuestBanner } from "./GuestBanner";
import { useUxMode, setUxMode, syncUxMode } from "../lib/uxMode";
import { ThemeToggle, AppearanceSetting } from "./ThemeToggle";
import { adoptServerPreference, useTheme, LIGHT_DARK_CHROME } from "../lib/theme";

export const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Mission Control", testid: "nav-home" },
      { to: "/onboarding/checklist", icon: Sparkles, label: "Pre-launch Checklist", testid: "nav-onboarding-checklist", badge: "New" },
      { to: "/ask-totalreach", icon: Sparkles, label: "Ask TotalReach", testid: "nav-ask-totalreach", badge: "AI" },
      { to: "/race-intelligence", icon: Landmark, label: "Race Intelligence", testid: "nav-race-intelligence", badge: "New" },
      { to: "/calendar", icon: CalendarDays, label: "Campaign Calendar", testid: "nav-calendar", badge: "New" },
      { to: "/team", icon: Users, label: "Team", testid: "nav-team" },
      { to: "/war-room", icon: WarRoomIcon, label: "Campaign War Room", testid: "nav-war-room" },
    ],
  },
  {
    label: "Advertise",
    items: [
      { to: "/campaigns/new", icon: Megaphone, label: "Create Campaign", testid: "nav-create-campaign" },
      { to: "/campaigns/ctv/new", icon: Tv, label: "CTV Campaigns", testid: "nav-ctv", badge: "Hero" },
      { to: "/planner/ctv", icon: Monitor, label: "CTV Voter Planner", testid: "nav-ctv-planner", badge: "New" },
      { to: "/campaigns", icon: ListChecks, label: "Campaigns", testid: "nav-campaigns" },
      { to: "/media-plans", icon: Layers, label: "Media Plans", testid: "nav-media-plans" },
      { to: "/email-campaigns", icon: Mail, label: "Email Campaigns", testid: "nav-email", badge: "New" },
      { to: "/direct-mail", icon: Mailbox, label: "Direct Mail", testid: "nav-direct-mail", badge: "New" },
      { to: "/journeys", icon: Route, label: "Journeys", testid: "nav-journeys", badge: "New" },
    ],
  },
  {
    label: "Target",
    items: [
      { to: "/target-voters", icon: Vote, label: "Target Voters", testid: "nav-target-voters" },
      { to: "/political-data", icon: Database, label: "Political Data", testid: "nav-political-data" },
      { to: "/audiences", icon: Users2, label: "My Audiences", testid: "nav-audiences" },
      { to: "/first-party", icon: FileText, label: "First-Party Data", testid: "nav-first-party" },
      { to: "/geo", icon: MapPinned, label: "District & Geo", testid: "nav-geo" },
      { to: "/other-targeting", icon: Compass, label: "Other Targeting", testid: "nav-other-targeting" },
    ],
  },
  {
    label: "Operate",
    items: [
      { to: "/operations", icon: ListChecks, label: "Tasks & Approvals", testid: "nav-operations", badge: "New" },
      { to: "/approvals/launch", icon: Rocket, label: "Launch Approvals", testid: "nav-launch-approvals", role: "staff", badge: "New" },
      { to: "/events", icon: CalendarDays, label: "Events", testid: "nav-events", badge: "New" },
      { to: "/volunteers", icon: HeartHandshake, label: "Volunteers", testid: "nav-volunteers" },
      { to: "/volunteers/leaderboard", icon: Trophy, label: "Volunteer Leaderboard", testid: "nav-volunteer-leaderboard", badge: "New" },
      { to: "/portfolio", icon: Building2, label: "Race Portfolio", testid: "nav-portfolio" },
      { to: "/agency", icon: Briefcase, label: "My Clients (Agency)", testid: "nav-agency", badge: "New" },
      { to: "/agency/team", icon: Users, label: "Agency Team & Access", testid: "nav-agency-team", badge: "New" },
      { to: "/brand-library", icon: Palette, label: "Brand Library", testid: "nav-brand-library", badge: "New" },
      { to: "/claims-vault", icon: FileCheck2, label: "Claims Vault", testid: "nav-claims-vault" },
      { to: "/qr-codes", icon: QrCode, label: "QR Codes", testid: "nav-qr-codes", badge: "New" },
      { to: "/ad-library", icon: Megaphone, label: "Political Ad Library", testid: "nav-ad-library" },
      { to: "/election-night", icon: Vote, label: "Election Night", testid: "nav-election-night" },
    ],
  },
  {
    label: "Fundraise",
    items: [
      { to: "/fundraising", icon: HandCoins, label: "Fundraising Dashboard", testid: "nav-fundraising" },
      { to: "/fundraising/campaigns", icon: HeartHandshake, label: "Fundraising Campaigns", testid: "nav-fr-campaigns" },
      { to: "/fundraising/landing-pages", icon: LayoutTemplate, label: "Landing Pages", testid: "nav-landing-pages" },
      { to: "/fundraising/microsites", icon: Globe, label: "Microsites", testid: "nav-microsites" },
      { to: "/fundraising/donors", icon: Users, label: "Donors", testid: "nav-donors" },
      { to: "/fundraising/transactions", icon: Receipt, label: "Transactions", testid: "nav-transactions" },
      { to: "/fundraising/economics", icon: TrendingUp, label: "Source Economics", testid: "nav-source-economics", badge: "New" },
      { to: "/fundraising/sms", icon: MessageSquare, label: "Text-to-Give SMS", testid: "nav-sms", badge: "New" },
    ],
  },
  {
    label: "Create",
    items: [
      { to: "/ad-studio", icon: Clapperboard, label: "Political Ad Studio", testid: "nav-ad-studio", badge: "New" },
      { to: "/ad-studio/refreshes", icon: Sparkles, label: "Fresh Creative Ideas", testid: "nav-ad-refreshes", badge: "AI" },
      { to: "/ai-assistant", icon: Sparkles, label: "AI Campaign Assistant", testid: "nav-ai-assistant" },
      { to: "/creative-studio", icon: Wand2, label: "AI Creative Studio", testid: "nav-creative-studio" },
      { to: "/ai-landing-page", icon: LayoutTemplate, label: "AI Landing Page Builder", testid: "nav-ai-landing" },
      { to: "/creatives", icon: Send, label: "Creatives Library", testid: "nav-creatives" },
    ],
  },
  {
    label: "Intelligence",
    items: [
      { to: "/political-intelligence", icon: BrainCircuit, label: "Political Intelligence", testid: "nav-political-intelligence", badge: "New" },
      { to: "/midterms", icon: Landmark, label: "Midterm Directory", testid: "nav-midterms", badge: "FEC" },
      { to: "/intelligence", icon: Activity, label: "Polling & Sentiment", testid: "nav-political" },
      { to: "/opponent", icon: Vote, label: "Opponent Intelligence", testid: "nav-opponent" },
    ],
  },
  {
    label: "Measure",
    items: [
      { to: "/optimize", icon: Gauge, label: "Optimization Agent", testid: "nav-optimize", badge: "New" },
      { to: "/reports", icon: LineChart, label: "Advertising Reports", testid: "nav-reports" },
      { to: "/reports/ctv", icon: Tv, label: "CTV Reports", testid: "nav-ctv-reports" },
    ],
  },
  {
    label: "Compliance",
    items: [
      { to: "/compliance", icon: ShieldCheck, label: "Compliance Center", testid: "nav-compliance" },
      { to: "/audit", icon: ScrollText, label: "Political Audit Vault", testid: "nav-audit" },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/organizations", icon: Building2, label: "Political Organizations", testid: "nav-orgs" },
      { to: "/billing", icon: Wallet, label: "Billing", testid: "nav-billing" },
      { to: "/pricing/admin", icon: Landmark, label: "Pricing Engine", testid: "nav-pricing", role: "admin" },
      { to: "/admin/promotions", icon: Gift, label: "Promotions & Credits", testid: "nav-promotions", role: "admin", badge: "New" },
      { to: "/admin/playbooks", icon: BookOpen, label: "Playbook Editor", testid: "nav-playbooks", role: "admin" },
      { to: "/ad-platform", icon: Layers, label: "Ad Platform (Admin)", testid: "nav-ad-platform", role: "admin" },
      { to: "/admin", icon: Settings, label: "Users & Settings", testid: "nav-admin" },
      { to: "/help", icon: HelpCircle, label: "Help & Glossary", testid: "nav-help" },
    ],
  },
];

const ProfileModal = ({ user, onClose }) => {
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", title: user?.title || "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/auth/me").then(r => setForm({ name: r.data.name || "", phone: r.data.phone || "", title: r.data.title || "" })).catch(() => {});
  }, []);

  const save = async () => {
    if (form.name.trim().length < 2) { setError("Name must be at least 2 characters"); return; }
    setBusy(true); setError("");
    try {
      const { data } = await api.put("/auth/profile", {
        name: form.name.trim(), phone: form.phone.trim(), title: form.title.trim(),
      });
      const stored = auth.getUser() || {};
      localStorage.setItem("tr_user", JSON.stringify({ ...stored, name: data.name, phone: data.phone, title: data.title }));
      toast.success("Profile updated");
      onClose();
      window.location.reload();
    } catch (e) { setError(e?.response?.data?.detail || "Could not update profile"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-app border border-white/10 rounded-lg max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="profile-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-white">My profile</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="profile-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1">Full name</label>
            <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} maxLength={80}
              data-testid="profile-name-input"
              className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1">Phone</label>
            <input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} maxLength={30} type="tel" placeholder="+1 555 000 0000"
              data-testid="profile-phone-input"
              className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-cyan-400/60 focus:outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1">Title / role</label>
            <input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} maxLength={80} placeholder="e.g. Campaign Manager"
              data-testid="profile-title-input"
              className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-cyan-400/60 focus:outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1">Email</label>
            <div className="text-sm text-slate-500 bg-slate-900/50 border border-white/10 rounded-md px-3 py-2" data-testid="profile-email-readonly">{user?.email}</div>
          </div>
          {error && <div className="text-xs text-rose-300" data-testid="profile-error">{error}</div>}
          <button onClick={save} disabled={busy} data-testid="profile-save-btn"
            className="w-full py-2 rounded-md bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold text-sm disabled:opacity-50">
            {busy ? "Saving…" : "Save profile"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ChangePasswordModal = ({ onClose }) => {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) { setError("New passwords don't match"); return; }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password updated");
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not update password");
    } finally { setBusy(false); }
  };
  const field = "w-full bg-slate-900/60 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:border-cyan-400 focus:outline-none";
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-app border border-white/10 rounded-lg max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="change-password-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold flex items-center gap-2"><KeyRound className="w-4 h-4 text-cyan-300" /> Change password</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <input type="password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} className={field} placeholder="Current password" required data-testid="cp-current-input" />
          <input type="password" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} className={field} placeholder="New password (min 8 characters)" required minLength={8} data-testid="cp-new-input" />
          <input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={field} placeholder="Confirm new password" required minLength={8} data-testid="cp-confirm-input" />
        </div>
        {error && <div className="mt-3 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md p-2.5" data-testid="cp-error">{error}</div>}
        <button type="submit" disabled={busy} data-testid="cp-submit-btn"
          className="mt-4 w-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold rounded-full px-4 py-2.5 text-sm hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Update password
        </button>
      </form>
    </div>
  );
};

const AI_ROUTES = ["/campaign-agent", "/creative-studio", "/ad-studio", "/ai-assistant", "/ai-landing-page"];

const SEARCH_TYPE_STYLES = {
  "Candidate": "text-cyan-300 border-cyan-400/30",
  "Donor": "text-emerald-300 border-emerald-400/30",
  "Campaign": "text-fuchsia-300 border-fuchsia-400/30",
  "Tracked race": "text-amber-300 border-amber-400/30",
};

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = auth.getUser();
  const plan = usePlan();
  const uxMode = useUxMode();
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  React.useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);
  React.useEffect(() => { Promise.resolve(syncUxMode()).then((d) => adoptServerPreference(d?.theme)); }, []);
  const { theme } = useTheme();
  const darkChrome = theme === "light" && LIGHT_DARK_CHROME;
  const switchMode = (m) => {
    if (m === uxMode) return;
    setUxMode(m);
    trackEvent("ux_mode_switch", { mode: m });
    if (m === "simple") navigate("/dashboard");
  };
  const [aiUsage, setAiUsage] = useState(null);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  // Sidebar nav — each group starts collapsed unless the user has manually
  // opened it before (persisted in localStorage). The group containing the
  // currently-active route auto-expands as a fallback.
  const [expandedOverrides, setExpandedOverrides] = useState(() => {
    const out = {};
    try {
      for (const g of NAV_GROUPS) {
        const raw = localStorage.getItem(`tr_nav_group_${g.label}`);
        if (raw === "1") out[g.label] = true;
        else if (raw === "0") out[g.label] = false;
      }
    } catch { /* no-op */ }
    return out;
  });
  const [navFilter, setNavFilter] = useState("");
  // Pinned favorites — user stars any nav item to keep it one click away.
  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = localStorage.getItem("tr_nav_favorites");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const toggleFavorite = (path) => {
    setFavorites((prev) => {
      const next = prev.includes(path) ? prev.filter((p) => p !== path) : [path, ...prev].slice(0, 8);
      try { localStorage.setItem("tr_nav_favorites", JSON.stringify(next)); } catch { /* no-op */ }
      trackEvent("sidebar_favorite_toggle", { path, favorited: !prev.includes(path) });
      return next;
    });
  };
  const [dragIndex, setDragIndex] = useState(null);
  const [showFeatureToggle, setShowFeatureToggle] = useState(null); // null | "all" | groupLabel
  // Feature hide/show — merged user + org-wide sets (org-hidden wins).
  const [hiddenFeatures, setHiddenFeatures] = useState(new Set());
  React.useEffect(() => {
    api.get("/app-settings/hidden-features")
      .then((r) => setHiddenFeatures(new Set([...(r.data?.user || []), ...(r.data?.org || [])])))
      .catch(() => {});
  }, []);
  // Admin-promoted "Quick Access" nav routes rendered in the header (fetched
  // once per session; auto-hidden when the admin hasn't promoted any).
  const [quickAccess, setQuickAccess] = useState([]);
  React.useEffect(() => {
    api.get("/app-settings/quick-access-routes")
      .then((r) => setQuickAccess((r.data?.routes || []).slice(0, 6)))
      .catch(() => setQuickAccess([]));
  }, []);
  const reorderFavorite = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx == null || toIdx == null) return;
    setFavorites((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      try { localStorage.setItem("tr_nav_favorites", JSON.stringify(arr)); } catch { /* no-op */ }
      trackEvent("sidebar_favorite_reorder", { path: moved, from: fromIdx, to: toIdx });
      return arr;
    });
  };
  // Recent pages — last 3 unique routes the user visited (persisted).
  const [recentPages, setRecentPages] = useState(() => {
    try {
      const raw = localStorage.getItem("tr_recent_pages");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  // Flat lookup of every nav item so recent-page pills / search results can
  // render the correct icon and label.
  const NAV_ITEM_INDEX = React.useMemo(() => {
    const map = {};
    for (const g of NAV_GROUPS) {
      for (const it of g.items) map[it.to] = { ...it, group: g.label };
    }
    return map;
  }, []);
  // Expose a compact label/group lookup on window so the dashboard's
  // PersonalTopPages widget can render friendly names for arbitrary routes
  // without duplicating the NAV_GROUPS constant.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const compact = {};
    for (const p of Object.keys(NAV_ITEM_INDEX)) {
      compact[p] = { label: NAV_ITEM_INDEX[p].label, group: NAV_ITEM_INDEX[p].group };
    }
    window.__tr_nav_index_cache__ = compact;
  }, [NAV_ITEM_INDEX]);
  // Track visited routes → recent pages (skip the current dashboard root so
  // it doesn't dominate every session). Also fires a nav_visit analytics event
  // so the admin can see the top-visited routes across all users, AND bumps
  // a per-user counter in localStorage powering the "Your top pages" widget.
  React.useEffect(() => {
    const p = location.pathname;
    if (!p || p === "/" || !NAV_ITEM_INDEX[p]) return;
    trackEvent("nav_visit", { path: p, label: NAV_ITEM_INDEX[p].label, group: NAV_ITEM_INDEX[p].group });
    setRecentPages((prev) => {
      const next = [p, ...prev.filter((x) => x !== p)].slice(0, 3);
      try { localStorage.setItem("tr_recent_pages", JSON.stringify(next)); } catch { /* no-op */ }
      return next;
    });
    // Per-user visit counter (drives the dashboard "Your most-visited pages" widget)
    try {
      const key = `tr_route_counts_${user?.id || "anon"}`;
      const raw = localStorage.getItem(key);
      const counts = raw ? JSON.parse(raw) : {};
      counts[p] = (counts[p] || 0) + 1;
      counts.__updated_at = new Date().toISOString();
      localStorage.setItem(key, JSON.stringify(counts));
    } catch { /* no-op */ }
  }, [location.pathname, NAV_ITEM_INDEX, user?.id]);
  const collapseAll = () => {
    const next = {};
    for (const g of NAV_GROUPS) next[g.label] = false;
    setExpandedOverrides(next);
    try {
      for (const g of NAV_GROUPS) localStorage.setItem(`tr_nav_group_${g.label}`, "0");
    } catch { /* no-op */ }
  };
  const filterQuery = navFilter.trim().toLowerCase();

  React.useEffect(() => {
    if (search.trim().length < 2) { setSearchResults(null); return undefined; }
    const t = setTimeout(() => {
      api.get(`/search/suggest?q=${encodeURIComponent(search.trim())}`)
        .then(r => setSearchResults(r.data.results))
        .catch(() => setSearchResults(null));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  React.useEffect(() => {
    api.get("/pricing/ai-usage/me").then(r => setAiUsage(r.data)).catch(() => {});
  }, [location.pathname]);

  const handleLogout = () => {
    auth.logout();
    navigate("/login");
  };

  if (!user) {
    navigate("/login");
    return null;
  }

  const isAdmin = user.role === "super_admin" || user.role === "admin";
  const isStaff = isAdmin || user.role === "campaign_manager";
  const canSee = (it) => !it.role || (it.role === "staff" ? isStaff : isAdmin);

  return (
    <div className="flex h-screen bg-app text-white overflow-hidden">
      {mobileNavOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden" data-testid="mobile-sidebar-backdrop"
          onClick={() => setMobileNavOpen(false)} />
      )}
      <aside data-theme={darkChrome ? "dark" : undefined} className={`w-64 shrink-0 border-r border-white/10 bg-sidebar flex-col fixed inset-y-0 left-0 z-50 lg:static lg:z-auto lg:flex ${mobileNavOpen ? "flex" : "hidden lg:flex"}`} data-testid="sidebar">
        <div className="h-16 flex items-center px-5 border-b border-white/10">
          <TotalReachLogo />
        </div>
        <div className="pt-3">
          <OrgSwitcher />
        </div>
        {uxMode === "simple" && <SimpleNavList />}
        {uxMode === "pro" && (<>
        {/* Sidebar search + Collapse all */}
        <div className="px-3 pt-3 pb-2 border-b border-white/5 space-y-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={navFilter}
              onChange={(e) => setNavFilter(e.target.value)}
              placeholder="Filter menu…"
              data-testid="sidebar-search"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md pl-7 pr-6 py-1.5 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-400/40"
            />
            {navFilter && (
              <button
                onClick={() => setNavFilter("")}
                data-testid="sidebar-search-clear"
                aria-label="Clear filter"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs px-1"
              >
                ×
              </button>
            )}
          </div>
          {!filterQuery && (
            <button
              onClick={collapseAll}
              data-testid="sidebar-collapse-all-btn"
              className="w-full text-[10px] uppercase tracking-widest text-slate-500 hover:text-cyan-300 py-1 rounded-md border border-white/10 hover:border-cyan-400/30 transition-colors"
            >
              Collapse all
            </button>
          )}
        </div>
        {/* Pinned favorites (hidden while filtering) */}
        {!filterQuery && favorites.length > 0 && (
          <div className="px-3 pt-3" data-testid="sidebar-favorites">
            <div className="px-2 mb-1 flex items-center gap-1.5">
              <Star className="w-3 h-3 text-amber-300 fill-amber-300" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                Favorites
              </span>
            </div>
            <div className="space-y-0.5">
              {favorites
                .map((p, idx) => ({ item: NAV_ITEM_INDEX[p], idx, path: p }))
                .filter((x) => !!x.item)
                .map(({ item: it, idx }) => (
                  <div
                    key={`fav-${it.to}`}
                    className={`group relative transition-opacity ${dragIndex === idx ? "opacity-40" : ""}`}
                    draggable
                    onDragStart={(e) => { setDragIndex(idx); e.dataTransfer.effectAllowed = "move"; }}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                    onDrop={(e) => { e.preventDefault(); reorderFavorite(dragIndex, idx); setDragIndex(null); }}
                    onDragEnd={() => setDragIndex(null)}
                    data-testid={`fav-row-${idx}`}
                  >
                    <NavLink
                      to={it.to}
                      data-testid={`fav-${it.testid}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-2.5 py-1.5 pr-7 rounded-md text-xs transition-colors cursor-grab active:cursor-grabbing ${
                          isActive
                            ? "bg-gradient-to-r from-cyan-400/15 to-blue-500/10 text-cyan-300 border border-cyan-400/20"
                            : "text-slate-300 hover:bg-white/5 hover:text-cyan-300 border border-transparent"
                        }`
                      }
                    >
                      <it.icon className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate flex-1">{it.label}</span>
                    </NavLink>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleFavorite(it.to); }}
                      data-testid={`fav-unpin-${it.testid}`}
                      title="Unpin"
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-amber-300 hover:text-amber-100 p-1"
                    >
                      <Star className="w-3 h-3 fill-amber-300" />
                    </button>
                  </div>
                ))}
            </div>
            {favorites.length > 1 && (
              <div className="text-[9px] text-slate-600 px-2 mt-1 italic">Drag to reorder</div>
            )}
          </div>
        )}
        {/* Recent pages (hidden while filtering) */}
        {!filterQuery && recentPages.length > 0 && (
          <div className="px-3 pt-3" data-testid="sidebar-recent">
            <div className="px-2 mb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              Recent
            </div>
            <div className="space-y-0.5">
              {recentPages
                .map((p) => NAV_ITEM_INDEX[p])
                .filter(Boolean)
                .map((it) => (
                  <NavLink
                    key={`recent-${it.to}`}
                    to={it.to}
                    data-testid={`recent-${it.testid}`}
                    className={({ isActive }) =>
                      `group flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-xs transition-colors ${
                        isActive
                          ? "bg-gradient-to-r from-cyan-400/15 to-blue-500/10 text-cyan-300 border border-cyan-400/20"
                          : "text-slate-300 hover:bg-white/5 hover:text-cyan-300 border border-transparent"
                      }`
                    }
                  >
                    <it.icon className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate flex-1">{it.label}</span>
                    <span className="text-[9px] text-slate-500">{it.group}</span>
                  </NavLink>
                ))}
            </div>
          </div>
        )}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1" data-testid="sidebar-nav">
          {NAV_GROUPS.map((group) => {
            const visibleItems = group.items.filter((it) =>
              (canSee(it)) && !hiddenFeatures.has(it.testid)
            );
            const groupHiddenCount = group.items.filter(
              (it) => (canSee(it)) && hiddenFeatures.has(it.testid)
            ).length;
            const matchedItems = filterQuery
              ? visibleItems.filter((it) => it.label.toLowerCase().includes(filterQuery))
              : visibleItems;
            if (matchedItems.length === 0) return null;
            // Auto-expand the group that contains the currently-active route so
            // the user always sees where they are — but keep every other group
            // collapsed by default. Manual open/close persists across page loads.
            // While filtering, force every matching group open.
            const containsActive = visibleItems.some(
              (it) => location.pathname === it.to || location.pathname.startsWith(it.to + "/")
            );
            const overrideKey = `tr_nav_group_${group.label}`;
            const override = expandedOverrides[group.label];
            const manualIsOpen = override === undefined ? containsActive : override;
            const isOpen = filterQuery ? true : manualIsOpen;
            const toggle = () => {
              if (filterQuery) return; // no-op while filtering
              const next = !isOpen;
              setExpandedOverrides((s) => ({ ...s, [group.label]: next }));
              try {
                localStorage.setItem(overrideKey, next ? "1" : "0");
              } catch { /* no-op */ }
            };
            return (
              <div key={group.label} data-testid={`sidebar-group-${group.label.toLowerCase()}`}>
                <button
                  type="button"
                  onClick={toggle}
                  data-testid={`sidebar-group-toggle-${group.label.toLowerCase()}`}
                  aria-expanded={isOpen}
                  disabled={!!filterQuery}
                  className={`w-full flex items-center justify-between px-2 py-2 rounded-md text-[10px] font-semibold uppercase tracking-widest transition-colors ${
                    isOpen ? "text-cyan-300" : "text-slate-500 hover:text-slate-300"
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    {group.label}
                    {containsActive && !filterQuery && (
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" title="Active section" />
                    )}
                    {filterQuery && (
                      <span className="text-[9px] text-cyan-300 font-mono normal-case">
                        {matchedItems.length}
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-1.5">
                    {groupHiddenCount > 0 && !filterQuery && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setShowFeatureToggle(group.label); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setShowFeatureToggle(group.label); } }}
                        data-testid={`sidebar-group-add-${group.label.toLowerCase()}`}
                        title={`Add ${groupHiddenCount} hidden item${groupHiddenCount === 1 ? "" : "s"} back`}
                        className="text-slate-400 hover:text-cyan-300 hover:bg-white/5 rounded p-0.5 inline-flex items-center gap-0.5"
                      >
                        <Plus className="w-3 h-3" />
                        <span className="text-[9px] font-mono normal-case">{groupHiddenCount}</span>
                      </span>
                    )}
                    {!filterQuery && (
                      <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                    )}
                  </span>
                </button>
                {isOpen && (
                  <div className="space-y-0.5 mt-0.5 mb-2">
                    {matchedItems.map((item) => {
                      const isFav = favorites.includes(item.to);
                      return (
                        <div key={item.to} className="group/item relative">
                          <NavLink
                            to={item.to}
                            end={item.to === "/"}
                            data-testid={item.testid}
                            className={({ isActive }) =>
                              `flex items-center gap-2.5 px-2.5 py-2 pr-8 rounded-md text-sm transition-colors ${
                                isActive
                                  ? "bg-gradient-to-r from-cyan-400/15 to-blue-500/10 text-cyan-300 border border-cyan-400/20"
                                  : "text-slate-300 hover:bg-white/5 hover:text-cyan-300 border border-transparent"
                              }`
                            }
                          >
                            <item.icon className="w-4 h-4 shrink-0" />
                            <span className="truncate flex-1">{item.label}</span>
                            {item.badge && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-bold">
                                {item.badge}
                              </span>
                            )}
                          </NavLink>
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(item.to); }}
                            data-testid={`pin-${item.testid}`}
                            title={isFav ? "Unpin from favorites" : "Pin to favorites"}
                            aria-label={isFav ? "Unpin" : "Pin"}
                            className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1 transition-opacity ${
                              isFav
                                ? "text-amber-300 opacity-100"
                                : "text-slate-500 opacity-0 group-hover/item:opacity-100 hover:text-amber-300"
                            }`}
                          >
                            <Star className={`w-3.5 h-3.5 ${isFav ? "fill-amber-300" : ""}`} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {filterQuery && (() => {
            const total = NAV_GROUPS.flatMap((g) => g.items.filter((it) => canSee(it)))
              .filter((it) => it.label.toLowerCase().includes(filterQuery)).length;
            if (total === 0) {
              return (
                <div className="px-3 py-6 text-center text-xs text-slate-500" data-testid="sidebar-no-results">
                  No menu items match &quot;{navFilter}&quot;
                </div>
              );
            }
            return null;
          })()}
        </nav>
        {hiddenFeatures.size > 0 && (
          <div className="px-3 pb-2">
            <button
              onClick={() => setShowFeatureToggle("all")}
              data-testid="sidebar-add-features-btn"
              className="w-full inline-flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest text-cyan-300 hover:text-cyan-100 py-1.5 rounded-md border border-cyan-400/20 hover:border-cyan-400/40 bg-cyan-400/[0.03]"
            >
              <Plus className="w-3 h-3" /> Add features ({hiddenFeatures.size} hidden)
            </button>
          </div>
        )}
        </>)}
        <div className="p-3 border-t border-white/10">
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm text-slate-300 hover:bg-rose-500/10 hover:text-rose-300 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>
      {showFeatureToggle && (
        <FeatureToggleModal
          navGroups={NAV_GROUPS.map((g) => ({
            label: g.label,
            items: g.items.filter((it) => canSee(it)),
          }))}
          scopeGroup={showFeatureToggle === "all" ? null : showFeatureToggle}
          onClose={() => setShowFeatureToggle(null)}
          onChange={(arr) => {
            setHiddenFeatures(new Set(arr));
            setShowFeatureToggle(null);
          }}
        />
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        {user?.is_guest && <GuestBanner user={user} />}
        {plan?.gated && plan?.no_subscription && (
          <div className="bg-gradient-to-r from-amber-500/15 to-amber-400/5 border-b border-amber-400/20 px-6 py-2 flex items-center justify-between gap-3" data-testid="upgrade-banner">
            <span className="text-xs text-amber-200">You&apos;re on the <strong>Starter preview</strong> — subscribe to unlock CTV, AI, Email, Direct Mail and the full political suite.</span>
            <button onClick={() => navigate("/billing")} data-testid="upgrade-banner-btn"
              className="text-xs font-semibold text-amber-300 hover:text-amber-100 inline-flex items-center gap-1 whitespace-nowrap">
              View plans <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
        )}
        {quickAccess.length > 0 && (
          <div className="border-b border-white/5 bg-white/[0.02]" data-testid="quick-access-bar">
            <div className="max-w-7xl mx-auto px-6 py-1.5 flex items-center gap-3 overflow-x-auto">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 shrink-0">Quick access</span>
              {quickAccess
                .map((p) => NAV_ITEM_INDEX[p])
                .filter(Boolean)
                .map((it) => (
                  <NavLink
                    key={`qa-${it.to}`}
                    to={it.to}
                    data-testid={`quick-access-${it.testid}`}
                    className={({ isActive }) =>
                      `shrink-0 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        isActive
                          ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-300"
                          : "border-white/10 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300"
                      }`
                    }
                  >
                    <it.icon className="w-3 h-3" /> {it.label}
                  </NavLink>
                ))}
            </div>
          </div>
        )}
        <header data-theme={darkChrome ? "dark" : undefined} className={`h-16 border-b border-white/10 ${darkChrome ? "bg-app/95" : "bg-app/70"} backdrop-blur-xl sticky top-0 z-30 flex items-center px-4 lg:px-6 gap-3 lg:gap-4`}>
          <button onClick={() => setMobileNavOpen(true)} data-testid="app-mobile-nav-toggle"
            className="lg:hidden text-slate-300 hover:text-white p-1.5 -ml-1" aria-label="Open menu">
            <MenuIcon className="w-5 h-5" />
          </button>
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={() => setTimeout(() => setSearchResults(null), 150)}
              placeholder="Search voters, campaigns, donors…"
              data-testid="global-search"
              className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2 text-sm placeholder-slate-500 focus:border-cyan-400/50 focus:outline-none"
            />
            {searchResults && (
              <div className="absolute top-full mt-2 left-0 right-0 bg-elevated border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-50 max-h-96 overflow-y-auto" data-testid="global-search-results">
                {searchResults.length === 0 ? (
                  <div className="px-4 py-3 text-xs text-slate-500">No matches for “{search}”</div>
                ) : searchResults.map((r, i) => (
                  <button key={i} data-testid={`search-result-${i}`}
                    onMouseDown={() => { navigate(r.link); setSearch(""); setSearchResults(null); }}
                    className="w-full text-left px-4 py-2.5 hover:bg-white/5 flex items-center gap-3 transition-colors">
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border shrink-0 ${SEARCH_TYPE_STYLES[r.type] || "text-slate-400 border-white/15"}`}>{r.type}</span>
                    <span className="min-w-0">
                      <span className="block text-sm text-white truncate">{r.label}</span>
                      {r.sublabel && <span className="block text-[11px] text-slate-500 truncate">{r.sublabel}</span>}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="inline-flex items-center rounded-full border border-white/10 bg-white/5 p-0.5" data-testid="mode-switcher" title="Simple: AI operates TotalReach for you. Pro: you operate it yourself.">
              <button onClick={() => switchMode("simple")} data-testid="mode-simple-btn"
                className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${uxMode === "simple" ? "bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900" : "text-slate-400 hover:text-white"}`}>
                Simple
              </button>
              <button onClick={() => switchMode("pro")} data-testid="mode-pro-btn"
                className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${uxMode === "pro" ? "bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900" : "text-slate-400 hover:text-white"}`}>
                Pro
              </button>
            </div>
            {aiUsage && (
              <button onClick={() => navigate("/billing")} data-testid="ai-usage-meter"
                title={`${aiUsage.calls} AI calls this month, billed at +${aiUsage.ai_premium_pct}% premium`}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-cyan-400/30 transition-colors text-xs">
                <Sparkles className="w-3 h-3 text-cyan-300" />
                <span className="text-slate-400">AI this mo:</span>
                <span className="text-white font-semibold">${Number(aiUsage.billed).toFixed(2)}</span>
              </button>
            )}
            <span className="hidden md:inline-flex pill pill-active">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-glow" /> Political mode
            </span>
            <ThemeToggle />
            <div className="relative">
              <button onClick={() => setMenuOpen(!menuOpen)} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-cyan-400/30 transition-colors" data-testid="user-menu">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-xs font-bold text-slate-900">
                  {(user.name || user.email).substring(0, 2).toUpperCase()}
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-xs font-medium text-white leading-tight">{user.name}</div>
                  <div className="text-[10px] text-slate-400 leading-tight capitalize">{user.role?.replace(/_/g, " ")}</div>
                </div>
                <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-52 rounded-lg border border-white/10 bg-app shadow-2xl z-50 py-1.5" data-testid="user-menu-dropdown">
                    <div className="px-3 py-2 border-b border-white/5">
                      <div className="text-xs font-medium text-white">{user.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">{user.email}</div>
                    </div>
                    <button onClick={() => { setMenuOpen(false); setEditingProfile(true); }} data-testid="menu-my-profile"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-cyan-300 transition-colors">
                      <UserRound className="w-3.5 h-3.5" /> My profile
                    </button>
                    <AppearanceSetting />
                    <button onClick={() => { setMenuOpen(false); setChangingPassword(true); }} data-testid="menu-change-password"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-cyan-300 transition-colors">
                      <KeyRound className="w-3.5 h-3.5" /> Change password
                    </button>
                    <button onClick={handleLogout} data-testid="menu-sign-out"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-rose-500/10 hover:text-rose-300 transition-colors">
                      <LogOut className="w-3.5 h-3.5" /> Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>
        <main className={`flex-1 overflow-y-auto ${uxMode === "simple" ? "pb-20 lg:pb-0" : ""}`} data-testid="main-content">
          <Outlet />
        </main>
        {uxMode === "simple" && <SimpleBottomBar />}
      </div>
      {changingPassword && <ChangePasswordModal onClose={() => setChangingPassword(false)} />}
      {editingProfile && <ProfileModal user={user} onClose={() => setEditingProfile(false)} />}
      <UpgradeNudge />
      <AnnualUpgradeToast />
      <AskTotalReachWidget />
      {(!plan?.gated || plan?.features?.ai) && AI_ROUTES.some(r => location.pathname.startsWith(r)) && (
        <AiDisclaimerGate onDecline={() => navigate("/dashboard")} />
      )}
    </div>
  );
}
